import { useEditor, useNode } from "@craftjs/core";
import { VideoCameraIcon } from "@heroicons/react/24/outline";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from "@mui/material";
import { useState } from "react";
import VideoContainer from "./components/Container";
import Settings from "./Settings";

export type VideoProps = {
    src?: string;
};

const HomeVideo = ({ src = "" }: Partial<VideoProps>) => {
    const {
        connectors: { connect },
        setProp,
    } = useNode();
    const { enabled } = useEditor((state) => ({ enabled: state.options.enabled }));

    const [showDialog, setShowDialog] = useState<boolean>(false);
    const [externalSrc, setExternalSrc] = useState<string>("");

    const addExternalMedia = () => {
        setShowDialog(false);
        setProp((props) => {
            props.src = externalSrc;
            props.mediaSource = true;
        });
        setExternalSrc("");
    };

    const closeDialog = () => {
        setShowDialog(false);
        setExternalSrc("");
    };

    const renderMedia = () => {
        if (!src) return null;

        if (src.includes("youtube") || src.includes("youtu.be")) {
            const videoId = src.split("v=")[1] || src.split("/").pop();

            return <VideoContainer enabled={enabled} src={`https://www.youtube.com/embed/${videoId}`} />;
        } else if (src.includes("vimeo")) {
            const videoId = src.split("/").pop();

            return <VideoContainer enabled={enabled} src={`https://player.vimeo.com/video/${videoId}`} />;
        } else if (src.includes("dailymotion")) {
            const videoId = src.split("/").pop();

            return <VideoContainer enabled={enabled} src={`https://www.dailymotion.com/embed/video/${videoId}`} />;
        }

        return <video src={src} controls className="w-full aspect-video" />;
    };

    return Boolean(src) || !enabled ? (
        <div ref={(ref) => connect(ref!)}>{renderMedia()}</div>
    ) : (
        <div
            ref={(ref) => connect(ref!)}
            className="flex flex-col items-center m-auto w-full aspect-video rounded-3xl bg-zinc-100 p-4 pointer-events-auto place-content-center"
        >
            <p className="flex items-center center text-2xl">
                <VideoCameraIcon className="h-6 w-6 mr-2" />
                Video
            </p>

            <p className="p-3 text-sm">Set a video to display.</p>
            <Button
                variant="outlined"
                sx={{ mt: 1 }}
                onClick={(e) => {
                    e.stopPropagation();
                    setShowDialog(true);
                }}
            >
                Add from URL
            </Button>

            <Dialog
                open={showDialog}
                onClose={() => setShowDialog(false)}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
                maxWidth={"sm"}
            >
                <form onSubmit={addExternalMedia}>
                    <DialogTitle>Insert Video</DialogTitle>
                    <DialogContent dividers>
                        <TextField
                            autoFocus
                            fullWidth
                            value={externalSrc}
                            onChange={(event) => setExternalSrc(event.target.value)}
                            label={`URL`}
                            type="text"
                            variant="standard"
                            sx={{ mb: 3 }}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => closeDialog()}>Cancel</Button>
                        <Button variant="contained" type="submit">
                            Add
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>
        </div>
    );
};

HomeVideo.craft = {
    displayName: "Video",
    props: {},
    rules: {
        canDrag: () => true,
    },
    related: {
        settings: Settings,
    },
};

export default HomeVideo;
